import type { BuildingComplete, BuildingType } from "components/BuildingProfile/types";

import { Dispatch, SetStateAction } from "react";

import { EpcExtractionData } from "../components/address-step/epc-modal/types";

export type AddressInfo = {
  address?: string;
  city?: string;
  country?: string;
  name?: string;
  postal_code?: string;
};

export type APIDeletedImage = FileWithInfoApi;

export type BuildingLayerItem = {
  id: string;
  name: string;
  preset: BuildingLayerPreset;
  subLayers: BuildingSubLayer[];
  visible: boolean;
};

export type BuildingLayerObject = {
  id: string;
  name: string;
  points: Point[];
};

export type BuildingLayerPreset = "custom" | "rooftop_objects" | "vegetation";

export type BuildingLayersFormValue = BuildingLayerItem[];
export type BuildingSubLayer = {
  id: string;
  name: string;
  nameFromPreset?: boolean;
  objects: BuildingLayerObject[];
};

export type BuildingTypeArray = { percentage: number; type: BuildingType | string }[];

export type Coordinates = {
  lat: number;
  lng: number;
};

export type Details = Partial<
  Pick<BuildingComplete, "building_type" | "construction_year" | "no_of_floors" | "rental_units">
> & {
  mixConfiguration?: MixConfiguration;
};

export type EnergyAnalysisFiles = Partial<Record<EnergyAnalysisFileSection, FileWithInfoLocal[]>>;

export type EnergyAnalysisFileSection =
  | "bills"
  | "buildingPlans"
  | "energyCertificate"
  | "otherDocuments"
  | "technicalData";

export type ExtraDetails = {
  has_cooling: boolean | undefined;
  has_pv: boolean | undefined;
  heating_system: HeatingKey | undefined;
  installation_date: number | undefined;
  mechanical_ventilation: boolean | undefined;
  modernizationElements: {
    facade: ModernizationElement;
    rooftop: ModernizationElement;
    windows: ModernizationElement;
  };
  modernized: boolean | undefined;
};

export type FileWithInfo = FileWithInfoApi | FileWithInfoLocal;

export type FileWithInfoApi = {
  id: string;
  source: "api";
  url?: string | undefined;
};

export type FileWithInfoLocal = {
  cloudPath?: string | undefined;
  file: File;
  id: string;
  source: "local";
};

export type HeatingKey = "district_heating" | "electric" | "gas_boiler" | "heat_pump" | "oil_boiler" | "other";

export type JobStatus = "completed" | "failed" | "pending" | "processing";

export type MapAddressSuggestion = {
  id: string;
  lat: number;
  lng: number;
  primary: string;
  secondary: string;
};

export type MixConfiguration = {
  [key in MixUseKey]: number;
};

export type MixUseKey = Exclude<BuildingType, "mixed">;

export type ModernizationElement = {
  modernization_date: number | undefined;
  modernized: boolean | undefined;
};

export type OutlinePolygon = Point[];

export type Point = [number, number];

export type SingleBuildingFormState = Partial<BuildingComplete> & {
  addressInfo?: AddressInfo;
  buildingCenter: Coordinates;
  deletedAPIImages?: APIDeletedImage[];
  details?: Details;
  epcExtractionData?: EpcExtractionData;
  epcRawExtractionData?: EpcExtractionData;
  extraDetails?: ExtraDetails;
  files?: EnergyAnalysisFiles;
  images: FileWithInfo[];
  layers?: BuildingLayersFormValue;
  outline?: OutlinePolygon;
  prefilledOutline?: boolean;
};

export const isFileFromApi = (file: FileWithInfo): file is FileWithInfoApi => file.source === "api";
export const isFileFromLocal = (file: FileWithInfo): file is FileWithInfoLocal => file.source === "local";

// ----- Server payloads (DTOs) -----

export type APIBuildingImage = {
  id: number;
  imageUrl: string;
  isCover: boolean;
  projectId: number;
  sortOrder: number;
  thumbnailUrl: string;
};

export type APIBuildingImages = {
  images: APIBuildingImage[];
};

export type APISubmitPayload = {
  buildingGeoLocation: {
    polygon: OutlinePolygon;
  };
  buildingType: { percentage: number; type: BuildingType }[];
  city: string;
  constructionYear: number | undefined;
  coolingAvailable: boolean | undefined;
  country: string;
  dtCreation: boolean;
  EPCFields: EpcExtractionData | undefined;
  fileInfo: Record<EnergyAnalysisFileSection, string[]>[] | undefined;
  floorAmount: number | undefined;
  hasModernizations: boolean | undefined;
  heatingSystem: HeatingKey | undefined;
  heatingSystemInstalledAt: string | undefined;
  imageList: string[];
  latitude: number;
  layerInfo:
    | undefined
    | {
        layerName: string;
        sublayers: {
          name: string;
          objects: {
            name: string;
            polygon: Point[];
          }[];
        }[];
      }[];
  longitude: number;
  mechanicalVentilation: boolean | undefined;
  modernizationFacadeAt: string;
  modernizationRooftopAt: string;
  modernizationWindowsAt: string;
  name: string;
  postCode: string;
  pvSystemAvailable: boolean | undefined;
  rentalUnits: number | undefined;
  street: string;
};

export type APIUpdatePayload = Omit<APISubmitPayload, "EPCFields"> & {
  projectId: string | undefined;
};

export type ContextValue = {
  callForRecenter: () => void;
  canExecuteDTCreation: boolean;
  defaultCenter: Coordinates;
  dismissEpcPrompt: () => void;
  formData: SingleBuildingFormState;
  hasDismissedEpcPrompt: boolean;
  isPrefillingFormData: boolean;
  jobId: string;
  onStartNewBuilding: () => void;
  outlineState: {
    isErrorOutline: boolean;
    isLoadingOutline: boolean;
  };
  pauseInteraction: boolean;
  projectId: string | undefined;
  recenterRequestFlag: number;
  setAddress: (address: AddressInfo) => void;
  setBuildingOutline: (outline: OutlinePolygon) => void;
  setCanExecuteDTCreation: Dispatch<SetStateAction<boolean>>;
  setFormData: Dispatch<SetStateAction<SingleBuildingFormState>>;
  setJobId: Dispatch<SetStateAction<string>>;
  setMapCenter: (center: Coordinates) => void;
  setOutlineState: (outlineState: { isErrorOutline: boolean; isLoadingOutline: boolean }) => void;
  setPauseInteractions: (pauseInteraction: boolean) => void;
  setStepNo: (stepNo: number, options?: SetStepNoOptions) => void;
  stepNo: number;
};

// ----- Submit / upload flow -----

export type CreateJobResponse = {
  error?: string;
  job_id?: string;
  project_id?: string;
  status?: string;
};

export type DetailsFieldId = "construction_year" | "no_of_floors" | "rental_units";

export type FileUploadProgress = {
  errorMessage?: string | undefined;
  percentage: number;
  status: "completed" | "error" | "progress";
};

export type JobPollResponse = {
  error: string;
  message: string;
  progress: number;
  project_id: string | undefined;
  status: JobStatus;
};

export type PrefillFormData = {
  building: {
    constructionYear: number | undefined;
    floorAmount: number | undefined;
    rentalUnits: number | undefined;
  };
  energy: {
    coolingAvailable: boolean | undefined;
    hasModernizations: boolean | undefined;
    heatingSystem: HeatingKey | undefined;
    heatingSystemInstalledAt: string | undefined;
    mechanicalVentilation: boolean | undefined;
    modernizationFacadeAt: string | undefined;
    modernizationRooftopAt: string | undefined;
    modernizationWindowsAt: string | undefined;
    pvSystemAvailable: boolean | undefined;
  };
  layers: {
    layerName: string;
    sublayers: {
      name: string;
      objects: {
        name: string;
        polygon: Point[];
      }[];
    }[];
  }[];
  location: {
    address: string;
    buildingPolygons: Point[] | undefined;
    city: string;
    country: string;
    latitude: number | undefined;
    longitude: number | undefined;
    postCode: string;
    street: string;
  };
  name: string;
  postCode: string;
  spatialInformation: Record<BuildingType, number> | undefined;
  street: string;
};

export type SetStepNoOptions = {
  replace?: boolean;
};

export type SummaryMixConfigurationModalProps = {
  handleSaveMixConfiguration: () => void;
  hasInvalidMixTotal: boolean;
  mixDraft: Record<MixUseKey, number>;
  mixError: string;
  mixModalOpen: boolean;
  mixTotal: number;
  setMixModalOpen: (open: boolean) => void;
  updateMixValue: (key: MixUseKey, value: number) => void;
};

export type SummaryStepDetailsContentProps = {
  detailsBuildingType?: BuildingType;
  mixConfiguration: MixConfiguration;
  onOpenMixConfiguration: () => void;
  updateDetailsField: (field: "building_type" | DetailsFieldId, value: string) => void;
};

export type SummaryStepSummaryContentProps = {
  handleImageChange: (file: File | undefined) => void;
  handleRemoveImage: (file: FileWithInfo) => void;
  images: SingleBuildingFormState["images"];
};

export type SummaryStepSummaryRow = { label: string; value: string };
